import { useState, useEffect } from 'react';
import { Chess } from 'chess.js';
import Square from './Square';
import PlayerBar from './PlayerBar';
import Toast from './Toast';
import { getGeminiMove, getGPTossMove, getNvidiaMove, getPlayerAIMove } from '../services/gameService';

const ChessBoard = ({ onGameEnd, onMoveUpdate, username, selectedModel = 1, aiName = 'MasterGemi', aiLogo, autoPlayEnabled = false, onAutoPlayMove, onDrawOffer }) => {
  const [chess] = useState(new Chess());
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [legalMoves, setLegalMoves] = useState([]);
  const [lastMove, setLastMove] = useState({ from: null, to: null });
  const [isCheck, setIsCheck] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [capturedPieces, setCapturedPieces] = useState({ white: [], black: [] });
  const [moveHistory, setMoveHistory] = useState([]);
  const [toast, setToast] = useState(null);
  const [geminiCommentary, setGeminiCommentary] = useState('');
  const [flatBoard, setFlatBoard] = useState([]);
  const [autoPlayThinking, setAutoPlayThinking] = useState(false);
  const [lastOpponentMove, setLastOpponentMove] = useState(null);
  const [movesWithoutProgress, setMovesWithoutProgress] = useState(0);
  const FIFTY_MOVE_LIMIT = selectedModel === 1 ? 15 : 50; // 15 for MasterGemi (Gemini), 50 for others

  // Convert chess.js board to flat array for rendering
  const getFlatBoard = () => {
    const flatBoard = [];
    const boardArray = chess.board();
    
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const square = String.fromCharCode(97 + col) + (8 - row);
        flatBoard.push({
          square,
          piece: boardArray[row][col],
          row,
          col
        });
      }
    }
    return flatBoard;
  };

  useEffect(() => {
    setFlatBoard(getFlatBoard());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-play effect - triggers when it's player's turn and autoPlay is enabled
  useEffect(() => {
    const runAutoPlay = async () => {
      if (autoPlayEnabled && chess.turn() === 'w' && !isThinking && !autoPlayThinking && !chess.isGameOver()) {
        setAutoPlayThinking(true);
        try {
          const response = await getPlayerAIMove(chess.fen(), lastOpponentMove);
          
          if (response && response.move) {
            // Execute the auto-play move
            const move = chess.move(response.move, { sloppy: true });
            
            if (move) {
              // Update state
              setLastMove({ from: move.from, to: move.to });
              setSelectedSquare(null);
              setLegalMoves([]);
              setFlatBoard(getFlatBoard());
              setIsCheck(chess.inCheck());
              
              // Notify parent about auto-play thinking
              onAutoPlayMove?.(response.thinking);
              
              // Update captured pieces
              if (move.captured) {
                const color = move.color === 'w' ? 'black' : 'white';
                setCapturedPieces(prev => ({
                  ...prev,
                  [color]: [...prev[color], move.captured]
                }));
              }

              // Update move history
              const newHistory = chess.history();
              setMoveHistory(newHistory);
              onMoveUpdate?.(newHistory, geminiCommentary);

              // Check for game end
              if (chess.isGameOver()) {
                handleGameEnd();
                return;
              }

              // Get opponent AI's response
              setIsThinking(true);
              await getAIResponse(move.san);
            }
          }
        } catch (error) {
          console.error('Auto-play error:', error);
          showToast("AutoPilot encountered an error", "error");
        } finally {
          setAutoPlayThinking(false);
        }
      }
    };

    // Small delay before auto-play kicks in
    const timer = setTimeout(runAutoPlay, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlayEnabled, chess.turn(), isThinking, autoPlayThinking]);

  // Show toast notification
  const showToast = (message, type = 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Handle square click
  const handleSquareClick = (square) => {
    // If auto-play is enabled, ignore manual clicks
    if (autoPlayEnabled) {
      showToast("AutoPilot is playing for you!", "warning");
      return;
    }

    // If it's MasterGemi's turn, ignore clicks
    if (chess.turn() === 'b') {
      showToast("Wait for MasterGemi to make a move!", "warning");
      return;
    }

    // If no piece selected, try to select this square
    if (!selectedSquare) {
      const piece = chess.get(square);
      
      // Check if there's a piece and it belongs to current player
      if (piece && piece.color === chess.turn()) {
        setSelectedSquare(square);
        
        // Get legal moves for this piece
        const moves = chess.moves({ square, verbose: true });
        const legalSquares = moves.map(move => move.to);
        setLegalMoves(legalSquares);
      } else if (piece) {
        showToast("That's not your piece!", "error");
      }
      return;
    }

    // Piece already selected, try to move
    if (selectedSquare === square) {
      // Clicked same square, deselect
      setSelectedSquare(null);
      setLegalMoves([]);
      return;
    }

    // Check if this square is a legal move
    if (!legalMoves.includes(square)) {
      // Not a legal move
      showToast("Invalid move! Select a highlighted square.", "error");
      
      // Try to select different piece instead
      const piece = chess.get(square);
      if (piece && piece.color === chess.turn()) {
        setSelectedSquare(square);
        const moves = chess.moves({ square, verbose: true });
        setLegalMoves(moves.map(move => move.to));
      } else {
        setSelectedSquare(null);
        setLegalMoves([]);
      }
      return;
    }

    // Execute the move
    executeMove(selectedSquare, square);
  };

  // Execute a move
  const executeMove = async (from, to) => {
    try {
      const move = chess.move({ from, to });
      
      if (!move) {
        showToast("Invalid move! Please try again.", "error");
        return;
      }

      // Update state
      setLastMove({ from, to });
      setSelectedSquare(null);
      setLegalMoves([]);
      setFlatBoard(getFlatBoard());
      setIsCheck(chess.inCheck());
      
      // Update captured pieces and track progress for 50-move rule
      if (move.captured || move.piece === 'p') {
        setMovesWithoutProgress(0); // Reset on capture or pawn move
        if (move.captured) {
          const color = move.color === 'w' ? 'black' : 'white';
          setCapturedPieces(prev => ({
            ...prev,
            [color]: [...prev[color], move.captured]
          }));
        }
      } else {
        setMovesWithoutProgress(prev => prev + 1);
      }

      // Update move history
      const newHistory = chess.history();
      setMoveHistory(newHistory);
      onMoveUpdate?.(newHistory, geminiCommentary);

      // Check for game end conditions
      if (chess.isGameOver()) {
        handleGameEnd();
        return;
      }
      
      // Check for 50-move rule (15 for MasterGemi)
      if (movesWithoutProgress + 1 >= FIFTY_MOVE_LIMIT * 2) {
        handleGameEnd(`Draw by ${FIFTY_MOVE_LIMIT}-move rule!`);
        return;
      }

      // Get MasterGemi's response
      setIsThinking(true);
      await getAIResponse(move.san);
      
    } catch (error) {
      console.error('Move error:', error);
      showToast("An error occurred. Please try again.", "error");
    }
  };

  // Get AI's move (Gemini or GPT-oss based on selected model)
  const getAIResponse = async (lastMove) => {
    try {
      const fen = chess.fen();
      
      // Select AI based on model
      let response;
      if (selectedModel === 2) {
        response = await getGPTossMove(fen, lastMove);
      } else if (selectedModel === 3) {
        response = await getNvidiaMove(fen, lastMove);
      } else {
        response = await getGeminiMove(fen, lastMove);
      }
      
      // Apply AI's move
      const aiMove = chess.move(response.move, { sloppy: true });
      
      if (!aiMove) {
        throw new Error('AI returned invalid move');
      }

      // Track opponent's last move for auto-play
      setLastOpponentMove(aiMove.san);

      // Update state
      setLastMove({ from: aiMove.from, to: aiMove.to });
      setFlatBoard(getFlatBoard());
      setIsCheck(chess.inCheck());
      
      // Debug log
      console.log('AI Response commentary:', response.commentary);
      
      // Set commentary with fallback
      const commentary = response.commentary || 'Analyzing the position...';
      setGeminiCommentary(commentary);
      
      // Update captured pieces and track progress for 50-move rule
      if (aiMove.captured || aiMove.piece === 'p') {
        setMovesWithoutProgress(0); // Reset on capture or pawn move
        if (aiMove.captured) {
          setCapturedPieces(prev => ({
            ...prev,
            white: [...prev.white, aiMove.captured]
          }));
        }
      } else {
        setMovesWithoutProgress(prev => prev + 1);
      }

      // Update move history
      const newHistory = chess.history();
      setMoveHistory(newHistory);
      onMoveUpdate?.(newHistory, commentary);

      // Check for game end conditions
      if (chess.isGameOver()) {
        handleGameEnd();
        return;
      }
      
      // Check for 50-move rule (15 for MasterGemi)
      if (movesWithoutProgress + 1 >= FIFTY_MOVE_LIMIT * 2) {
        handleGameEnd(`Draw by ${FIFTY_MOVE_LIMIT}-move rule!`);
        return;
      }
      
    } catch (error) {
      console.error('AI move error:', error);
      showToast("AI encountered an error. Please refresh.", "error");
    } finally {
      setIsThinking(false);
    }
  };

  // Check for insufficient material
  const hasInsufficientMaterial = () => {
    const pieces = chess.board().flat().filter(p => p !== null);
    
    // King vs King
    if (pieces.length === 2) return true;
    
    // King + Bishop vs King or King + Knight vs King
    if (pieces.length === 3) {
      const minorPiece = pieces.find(p => p.type === 'b' || p.type === 'n');
      if (minorPiece) return true;
    }
    
    // King + Bishop vs King + Bishop (same color bishops)
    if (pieces.length === 4) {
      const bishops = pieces.filter(p => p.type === 'b');
      if (bishops.length === 2) {
        // Check if both bishops are on same color squares
        // This is a simplified check
        return true;
      }
    }
    
    return false;
  };

  // Get draw reason
  const getDrawReason = () => {
    if (chess.isStalemate()) return 'Stalemate! No legal moves but not in check.';
    if (chess.isThreefoldRepetition()) return 'Draw by threefold repetition!';
    if (chess.isInsufficientMaterial() || hasInsufficientMaterial()) return 'Draw by insufficient material!';
    if (movesWithoutProgress >= FIFTY_MOVE_LIMIT * 2) return `Draw by ${FIFTY_MOVE_LIMIT}-move rule! No captures or pawn moves.`;
    if (chess.isDraw()) return 'Game ended in a draw!';
    return 'Draw!';
  };

  // Handle game end
  const handleGameEnd = (drawReason = null) => {
    let result;
    let commentary = geminiCommentary;
    
    if (chess.isCheckmate()) {
      result = chess.turn() === 'w' ? 'loss' : 'win';
      commentary = chess.turn() === 'w' ? `${aiName} wins by checkmate!` : 'You won by checkmate!';
    } else if (chess.isDraw() || chess.isStalemate() || chess.isThreefoldRepetition() || chess.isInsufficientMaterial() || hasInsufficientMaterial()) {
      result = 'draw';
      commentary = drawReason || getDrawReason();
    } else if (movesWithoutProgress >= FIFTY_MOVE_LIMIT * 2) {
      result = 'draw';
      commentary = `Draw by ${FIFTY_MOVE_LIMIT}-move rule! No captures or pawn moves.`;
    } else if (drawReason) {
      result = 'draw';
      commentary = drawReason;
    }

    onGameEnd(result, moveHistory, chess.fen(), commentary);
  };

  // Handle draw offer acceptance (called from parent)
  const acceptDrawOffer = () => {
    handleGameEnd('Draw by mutual agreement!');
  };

  // Expose acceptDrawOffer to parent via callback
  useEffect(() => {
    if (onDrawOffer) {
      onDrawOffer(acceptDrawOffer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onDrawOffer]);

  // Handle escape key to deselect
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setSelectedSquare(null);
        setLegalMoves([]);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  return (
    <div className="flex-1 flex items-center justify-center p-3 bg-background dark:bg-dark-background relative overflow-hidden">
      {/* Toast notifications */}
      {toast && <Toast message={toast.message} type={toast.type} />}
      
      {/* Thinking overlay - only show for opponent AI, not AutoPilot */}
      {isThinking && !autoPlayEnabled && (
        <div className="absolute inset-0 bg-black bg-opacity-70 flex items-center justify-center z-10 backdrop-blur-sm">
          <div className="bg-background dark:bg-dark-surface border-3 border-border-heavy dark:border-dark-border-heavy p-8 flex items-center gap-5 shadow-brutal dark:shadow-brutal-dark">
            <div className="w-8 h-8 border-3 border-accent dark:border-dark-accent border-t-transparent animate-spin" />
            <span className="text-text-primary dark:text-dark-text-primary font-medium text-base">{aiName} is Thinking...</span>
          </div>
        </div>
      )}

      <div className="flex flex-col">
        {/* Top player (AI) */}
        <PlayerBar 
          playerName={aiName}
          playerColor="accent"
          capturedPieces={capturedPieces.white}
          isTop={true}
          playerLogo={aiLogo}
        />
        
        {/* Board Container with Labels */}
        <div className="relative px-10 py-6">
          {/* File labels - Top (a-h) */}
          <div className="absolute top-1 left-10 right-10 flex justify-around text-text-primary dark:text-dark-text-primary text-xs font-mono font-bold">
            {['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'].map(file => (
              <span key={`top-${file}`} className="w-[62px] text-center">{file}</span>
            ))}
          </div>
          
          {/* File labels - Bottom (a-h) */}
          <div className="absolute bottom-1 left-10 right-10 flex justify-around text-text-primary dark:text-dark-text-primary text-xs font-mono font-bold">
            {['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'].map(file => (
              <span key={`bottom-${file}`} className="w-[62px] text-center">{file}</span>
            ))}
          </div>
          
          {/* Rank labels - Left (8-1) */}
          <div className="absolute left-1 top-6 bottom-6 flex flex-col justify-around text-text-primary dark:text-dark-text-primary text-xs font-mono font-bold">
            {[8, 7, 6, 5, 4, 3, 2, 1].map(rank => (
              <span key={`left-${rank}`} className="h-[62px] flex items-center justify-center w-8">{rank}</span>
            ))}
          </div>
          
          {/* Rank labels - Right (8-1) */}
          <div className="absolute right-1 top-6 bottom-6 flex flex-col justify-around text-text-primary dark:text-dark-text-primary text-xs font-mono font-bold">
            {[8, 7, 6, 5, 4, 3, 2, 1].map(rank => (
              <span key={`right-${rank}`} className="h-[62px] flex items-center justify-center w-8">{rank}</span>
            ))}
          </div>
          
          {/* Board */}
          <div className="bg-border-heavy dark:bg-dark-border-heavy p-1 border-3 border-border-heavy dark:border-dark-border-heavy shadow-brutal dark:shadow-brutal-dark">
            {/* 8x8 Board */}
            <div className="grid grid-cols-8 gap-0 w-[496px] h-[496px]">
              {flatBoard.map((squareData) => (
                <Square
                  key={squareData.square}
                  square={squareData.square}
                  piece={squareData.piece}
                  isLight={(squareData.row + squareData.col) % 2 === 0}
                  isSelected={selectedSquare === squareData.square}
                  isLegalMove={legalMoves.includes(squareData.square)}
                  isLastMove={lastMove.from === squareData.square || lastMove.to === squareData.square}
                  isCheck={isCheck && squareData.piece?.type === 'k' && squareData.piece?.color === chess.turn()}
                  onClick={() => handleSquareClick(squareData.square)}
                />
              ))}
            </div>
          </div>
        </div>
        
        {/* Bottom player (User) */}
        <PlayerBar 
          playerName={username}
          playerColor="accent"
          capturedPieces={capturedPieces.black}
          isTop={false}
        />
      </div>
    </div>
  );
};

export default ChessBoard;
